import { useCallback, useEffect, useState } from 'react'
import {
  getFeed,
  removeNotification,
  saveStatistic,
  sendInAppEvent
} from '@/api/api'
import {
  FEED_PER_PAGE,
  FeedPostNotification,
  IBranding
} from '@/feed/Feed.interface'
import useInfiniteScroll from '@/hooks/useInfiniteScroll'
import BrandedToolbar, {
  isToolbarHidden
} from '@/components/Toolbar/BrandedToolbar'
import SwipeableListItem from '@/components/SwipeableListItem'
import EmptyFeed from '@/components/error/EmptyFeed'
import FeedItem from './FeedItem'

interface IProps {
  hasNextPage: boolean
  feedFirstPage: FeedPostNotification[]
  pageParams: {
    sdkAppId: string
    sdkAppKey: string
    alias: string
  }
  sendClickStat: ({
    campaignGuid,
    buttonNumber
  }: {
    campaignGuid: string
    buttonNumber: number
  }) => void
  sendDeleteFeedpostStat: ({ campaignGuid }: { campaignGuid: string }) => void
  branding: IBranding | null
}

function Feed({
  hasNextPage,
  feedFirstPage,
  sendClickStat,
  sendDeleteFeedpostStat,
  pageParams,
  branding
}: IProps) {
  const [page, setPage] = useState(1)
  const [feed, setFeed] = useState(feedFirstPage)
  const [hasMore, setHasMore] = useState(hasNextPage)

  const { alias, sdkAppId, sdkAppKey } = pageParams
  const key = `${sdkAppId}${sdkAppKey}`

  const loadMore = async () => {
    const newPage = page + 1
    try {
      const response = await getFeed({
        params: {
          alias,
          page: newPage,
          perPage: FEED_PER_PAGE
        },
        key
      })
      setFeed([...feed, ...response.data.inboxItems])
      setPage(newPage)
      setHasMore(response.data.inboxItems.length === FEED_PER_PAGE)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  const onSendInAppEvent = useCallback(
    async (inAppEvents: { name: string; occurredAt: string }[]) => {
      try {
        await sendInAppEvent({
          data: {
            alias,
            inAppEvents
          },
          key
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    [alias, key]
  )

  // Kept stable: FeedItem fires the impression from an effect that depends on
  // this identity, so a fresh closure per render would re-send on every render.
  const sendImpressionStat = useCallback(
    async ({ campaignGuid }: { campaignGuid: string }) => {
      try {
        await saveStatistic({
          key,
          data: {
            campaignGuid,
            alias,
            key: 'card_front_impression'
          }
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    [alias, key]
  )

  const onRemoveNotification = async ({
    guid,
    campaignsGuids
  }: {
    guid: string
    campaignsGuids: string[]
  }) => {
    const previousFeed = feed
    // Optimistically remove the notification from the UI
    setFeed((prevFeed) => prevFeed.filter((item) => item.guid !== guid))

    try {
      await removeNotification({
        data: {
          guid,
          campaignsGuids,
          alias
        },
        key
      })

      await sendDeleteFeedpostStat({
        campaignGuid: campaignsGuids[0]
      })
    } catch (error) {
      // Roll back UI change if deletion fails
      setFeed(previousFeed)
      // eslint-disable-next-line no-console
      console.error('Failed to remove notification', error)
    }
  }

  const bottomRef = useInfiniteScroll({
    hasMore,
    onLoadMore: loadMore
  })

  useEffect(() => {
    setFeed(feedFirstPage)
    setPage(1)
    setHasMore(hasNextPage)
  }, [feedFirstPage, hasNextPage])

  const hideToolbar = isToolbarHidden(branding)

  if (feed.length === 0) {
    return (
      <>
        <BrandedToolbar branding={branding} />
        <div className={`${hideToolbar ? 'pt-0' : 'pt-[55px]'}`}>
          <EmptyFeed
            withBackButton={
              branding?.enabled && !branding?.feedHideGoBackToMainButton
            }
          />
        </div>
      </>
    )
  }

  return (
    <div className={`${hideToolbar ? 'pt-0' : 'pt-1 pb-1 bg-blue-300'}`}>
      <BrandedToolbar branding={branding} />
      <div className={`${hideToolbar ? 'pt-0' : 'pt-[55px]'}`}>
        {feed.map((feedItem, i) => (
          <div
            className="mb-1 last:mb-0"
            key={
              typeof feedItem.guid === 'string'
                ? feedItem.guid
                : `${feedItem.campaignGuid}-${i}`
            }
            ref={i === feed.length - 1 ? bottomRef : null}>
            <SwipeableListItem
              onDelete={() =>
                onRemoveNotification({
                  guid: feedItem.guid,
                  campaignsGuids: [feedItem.campaignGuid]
                })
              }>
              <FeedItem
                sendImpressionStat={sendImpressionStat}
                sendInAppEvent={onSendInAppEvent}
                sendClickStat={sendClickStat}
                campaignGuid={feedItem.campaignGuid}
                lastInteractionAt={feedItem.lastInteractionAt}
                feedPostFront={feedItem.front}
              />
            </SwipeableListItem>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Feed
